/**
 * Artist details and related API calls
 */

import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import type { ArtistDetailsResponse } from "@/types/artistDetails";
import type { ArtistInstagramConnectResponse } from "@/api/types";
import { useInstagramStore } from "@/stores/instagramStore";

export interface GalleryImageItem {
  id: number;
  order: number;
  url: string;
}

export interface GalleryImagesResponse {
  success: boolean;
  data: GalleryImageItem[];
}

export interface GalleryVideoItem {
  id: number;
  url: string;
  [key: string]: unknown;
}

// --- Awards & Achievements types ---

export interface ArtistAwardApiItem {
  id?: number;
  type: string;
  name: string;
  description: string | null;
  date: string | null;
  organization: string | null;
}

export interface ArtistAwardsResponse {
  success: boolean;
  data: ArtistAwardApiItem[];
}

// --- Past Performances types ---

export interface PastPerformanceApiItem {
  id: number;
  event_count: number;
  event_type: number;
  event_name: string;
  desc: string;
}

export interface PastPerformancesResponse {
  success: boolean;
  data: PastPerformanceApiItem[];
}

export interface PastPerformancePutItem {
  event_count: number;
  event_type: number;
  desc: string;
}

// --- Contact details types (primary, secondary, update requests) ---

export interface ContactDetailsPrimary {
  phone: string | null;
  email: string | null;
  phone_verified: boolean;
  email_verified: boolean;
  update_request?: {
    email?: boolean;
    phone?: boolean;
  } | null;
}

export interface ContactDetailsSecondaryItem {
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface ContactDetailsUpdateRequestItem {
  email: string | null;
  phone: string | null;
  status: number;
  status_display: string;
  created_at: string;
}

export interface ContactDetailsData {
  primary: ContactDetailsPrimary;
  secondary: ContactDetailsSecondaryItem[];
  contact_details_update_requests: ContactDetailsUpdateRequestItem[];
}

export interface ContactDetailsResponse {
  success: boolean;
  data: ContactDetailsData;
}

export interface ContactUsPayload {
  inquiry_message: string;
  message: string;
}

// --- Performance details types ---

export interface PerformanceLanguageItem {
  language_name: string;
  l_slug: string;
}

export interface PerformanceMemberGender {
  gender: string;
  g_slug: string;
}

export interface PerformanceMemberItem {
  id?: number;
  name: string;
  role: string;
  age: number;
  gender: PerformanceMemberGender | string;
}

export interface PerformanceDetailsData {
  performing_language?: PerformanceLanguageItem[];
  min_duration_mins?: number;
  max_duration_mins?: number;
  open_to_travel?: string | null;
  offstage_crew?: number | null;
  performing_members?: PerformanceMemberItem[];
}

export interface PerformanceDetailsPayload {
  performing_language?: string[];
  min_duration_mins?: number;
  max_duration_mins?: number;
  open_to_travel?: string;
  offstage_crew?: number;
  performing_members?: Array<{
    name: string;
    role: string;
    age: number;
    gender_slug: string;
  }>;
}

export class ArtistService {
  /**
   * Fetch artist details (api/v2/artist/details/).
   * Used after onboarding for Manager/Artist/Family and to populate sidebar/profile.
   */
  static async getArtistDetails(): Promise<ArtistDetailsResponse> {
    const response = await apiClient.get<ArtistDetailsResponse>(
      API_ENDPOINTS.AUTH.ARTIST_DETAILS
    );
    return response.data;
  }

  /**
   * Update profile picture (api/v2/artist/profile-pic/).
   * Sends image in multipart/form-data payload. Call getArtistDetails() after to refresh UI.
   */
  static async updateProfilePic(blob: Blob): Promise<void> {
    const formData = new FormData();
    const fileName =
      // @ts-expect-error name may exist on Blob/File
      (blob as any).name || `profile.${blob.type?.split("/")[1] || "jpg"}`;
    formData.append("image", blob, fileName);

    await apiClient.patch(API_ENDPOINTS.AUTH.PROFILE_PIC, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  /**
   * Get gallery images (api/v2/artist/gallery/images/).
   */
  static async getGalleryImages(): Promise<GalleryImageItem[]> {
    const response = await apiClient.get<GalleryImagesResponse | GalleryImageItem[]>(
      API_ENDPOINTS.AUTH.ARTIST_GALLERY_IMAGES
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    return (data as GalleryImagesResponse)?.data ?? [];
  }

  /**
   * Add gallery images via POST (api/v2/artist/gallery/images/).
   * Send FormData with image file(s). Returns updated list with id, order, url.
   */
  static async addGalleryImages(formData: FormData): Promise<GalleryImageItem[]> {
    try {
      const response = await apiClient.post<GalleryImagesResponse>(
        API_ENDPOINTS.AUTH.ARTIST_GALLERY_IMAGES,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data?.data ?? [];
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMessage: string | undefined =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message;

      if (status === 400 && apiMessage) {
        throw new Error(apiMessage);
      }

      throw err;
    }
  }

  /**
   * Add gallery images via media links (api/v2/artist/gallery/images/).
   * Body: { media_links: string[] } – used for importing from Instagram.
   */
  static async addGalleryImagesFromLinks(mediaLinks: string[]): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_GALLERY_IMAGES, {
        media_links: mediaLinks,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMessage: string | undefined =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message;

      // Surface 400 error message from API (e.g. upload limit exceeded)
      if (status === 400 && apiMessage) {
        throw new Error(apiMessage);
      }

      throw err;
    }
  }

  /**
   * Delete a gallery image (api/v2/artist/gallery/images/{id}/).
   */
  static async deleteGalleryImage(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_GALLERY_IMAGE_DELETE(id));
  }

  /**
   * Update gallery image order (PUT api/v2/artist/gallery/images/).
   * Payload: { order: [id1, id2, ...] } in desired order.
   */
  static async updateGalleryOrder(order: number[]): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_GALLERY_IMAGES, { order });
  }

  // --- Gallery Videos ---

  /**
   * Get gallery videos (api/v2/artist/gallery/videos/).
   */
  static async getGalleryVideos(): Promise<GalleryVideoItem[]> {
    const response = await apiClient.get<
      { data?: GalleryVideoItem[] } | GalleryVideoItem[]
    >(API_ENDPOINTS.AUTH.ARTIST_GALLERY_VIDEOS);
    const data = response.data;
    if (Array.isArray(data)) return data;
    return (data as { data?: GalleryVideoItem[] })?.data ?? [];
  }

  /**
   * Add gallery videos via POST (api/v2/artist/gallery/videos/).
   * Body: { media_links: string[] }
   */
  static async addGalleryVideos(mediaLinks: string[]): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_GALLERY_VIDEOS, {
        media_links: mediaLinks,
      });
    } catch (err: any) {
      const status = err?.response?.status;
      const apiMessage: string | undefined =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message;

      // Surface 400 error message from API (e.g. upload limit exceeded)
      if (status === 400 && apiMessage) {
        throw new Error(apiMessage);
      }

      throw err;
    }
  }

  /**
   * Exchange Instagram OAuth code for long-lived token (POST artist/instagram/connect/).
   * Returns connect response; does not update store. Use connectInstagramAndStore for Bridge.
   */
  static async postArtistInstagramConnect(code: string): Promise<ArtistInstagramConnectResponse["data"]> {
    const response = await apiClient.post<ArtistInstagramConnectResponse>(
      API_ENDPOINTS.SOCIAL.ARTIST_INSTAGRAM_CONNECT,
      { code }
    );
    if (!response.data?.success || !response.data?.data) {
      throw new Error(response.data?.data?.message ?? "Instagram connect failed.");
    }
    return response.data.data;
  }

  /**
   * Call artist Instagram connect API and store token in instagramStore (single source of truth).
   * Use from Bridge after OAuth callback for context connect/photos.
   */
  static async connectInstagramAndStore(code: string): Promise<void> {
    const data = await this.postArtistInstagramConnect(code);
    const store = useInstagramStore.getState();
    await store.setConnection({
      connected: true,
      instagramId: data.user_id,
      username: data.username,
      accessToken: data.token,
    });
  }

  /**
   * Delete a gallery video (api/v2/artist/gallery/videos/{id}/).
   */
  static async deleteGalleryVideo(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_GALLERY_VIDEO_DELETE(id));
  }

  // --- Awards & Achievements ---

  /**
   * Get awards & achievements (api/v2/artist/awards/).
   * Backend returns a mixed list with `type` field ("award" | "achievement").
   */
  static async getAwards(): Promise<ArtistAwardApiItem[]> {
    const response = await apiClient.get<ArtistAwardsResponse>(
      API_ENDPOINTS.AUTH.ARTIST_AWARDS
    );
    if (response.data?.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  }

  /**
   * Add awards & achievements (POST api/v2/artist/awards/).
   * Payload mirrors GET shape; send the new items to add.
   */
  static async updateAwards(items: ArtistAwardApiItem[]): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_AWARDS, {
      data: items,
    });
  }

  /**
   * Delete a single award or achievement (DELETE api/v2/artist/awards/:id/).
   */
  static async deleteAward(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_AWARD_DELETE(id));
  }

  // --- Past Performances ---

  /**
   * Get past performances (GET api/v2/artist/past-performances/).
   */
  static async getPastPerformances(): Promise<PastPerformanceApiItem[]> {
    const response = await apiClient.get<PastPerformancesResponse>(
      API_ENDPOINTS.AUTH.ARTIST_PAST_PERFORMANCES
    );
    if (response.data?.success && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  }

  /**
   * Add past performances (PUT api/v2/artist/past-performances/).
   * Payload: { performances: [{ event_count, event_type, desc }] }.
   */
  static async updatePastPerformances(
    performances: PastPerformancePutItem[]
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_PAST_PERFORMANCES, {
      performances,
    });
  }

  /**
   * Delete a past performance (DELETE api/v2/artist/past-performances/:id/).
   */
  static async deletePastPerformance(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_PAST_PERFORMANCE_DELETE(id));
  }

  // --- Personal details ---

  /** GET api/v2/artist/contact-details/ */
  static async getContactDetails(): Promise<ContactDetailsData | null> {
    const response = await apiClient.get<ContactDetailsResponse>(
      API_ENDPOINTS.AUTH.ARTIST_CONTACT_DETAILS
    );
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** POST api/v2/artist/contact-details/ – secondary contact and primary change requests */
  static async updateContactDetailsSecondary(payload: {
    email: string | null;
    mobile: string | null;
  }): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_DETAILS, {
      secondary: [
        {
          email: payload.email,
          mobile: payload.mobile,
        },
      ],
    });
  }

  /** GET api/v2/artist/personal-details/ */
  static async getPersonalDetails(): Promise<PersonalDetailsData | null> {
    const response = await apiClient.get<{
      success: boolean;
      data: PersonalDetailsData;
    }>(API_ENDPOINTS.AUTH.PERSONAL_DETAILS);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** PUT api/v2/artist/personal-details/ */
  static async updatePersonalDetails(
    payload: PersonalDetailsPayload
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.PERSONAL_DETAILS, payload);
  }

  // --- Performance details ---

  /** GET api/v2/artist/performance-details/ */
  static async getPerformanceDetails(): Promise<PerformanceDetailsData | null> {
    const response = await apiClient.get<{
      success: boolean;
      data: PerformanceDetailsData;
    }>(API_ENDPOINTS.AUTH.PERFORMANCE_DETAILS);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** PUT api/v2/artist/performance-details/ */
  static async updatePerformanceDetails(
    payload: PerformanceDetailsPayload
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.PERFORMANCE_DETAILS, payload);
  }

  /** DELETE api/v2/artist/performance-details/performing-members/{id}/ */
  static async deletePerformingMember(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.PERFORMING_MEMBER_DELETE(id));
  }

  // --- USP ---

  /** GET api/v2/artist/usp/ */
  static async getUsp(): Promise<UspData | null> {
    const response = await apiClient.get<{
      success: boolean;
      data: UspData;
    }>(API_ENDPOINTS.AUTH.ARTIST_USP);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** PUT api/v2/artist/usp/ */
  static async updateUsp(payload: UspPayload): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_USP, payload);
  }

  // --- Story ---

  /** GET api/v2/artist/story/ */
  static async getStory(): Promise<StoryData | null> {
    const response = await apiClient.get<{
      success: boolean;
      data: StoryData;
    }>(API_ENDPOINTS.AUTH.ARTIST_STORY);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** PUT api/v2/artist/story/ */
  static async updateStory(payload: StoryPayload): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_STORY, payload);
  }

  /** GET api/v2/artist/event-pricing/ */
  static async getEventPricing(): Promise<EventPricingApiItem[]> {
    const response = await apiClient.get<EventPricingApiResponse>(
      API_ENDPOINTS.AUTH.ARTIST_EVENT_PRICING
    );
    if (response.data?.success && Array.isArray(response.data?.data)) {
      return response.data.data;
    }
    return [];
  }

  /** POST api/v2/artist/event-pricing/ */
  static async createEventPricing(
    payload: EventPricingCreatePayload
  ): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_EVENT_PRICING, payload);
  }

  /** PATCH api/v2/artist/event-pricing/{slug}/ */
  static async updateEventPricing(
    slug: string,
    payload: EventPricingUpdatePayload
  ): Promise<void> {
    await apiClient.patch(
      API_ENDPOINTS.AUTH.ARTIST_EVENT_PRICING_DELETE(slug),
      payload
    );
  }

  /** DELETE api/v2/artist/event-pricing/{slug}/ */
  static async deleteEventPricing(slug: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_EVENT_PRICING_DELETE(slug));
  }

  /** PATCH api/v2/artist/special-event-pricing/{slug}/ */
  static async patchSpecialEventPricing(
    slug: string,
    payload: { festival_pricing: Record<string, string> }
  ): Promise<void> {
    await apiClient.patch(
      API_ENDPOINTS.AUTH.ARTIST_SPECIAL_EVENT_PRICING(slug),
      payload
    );
  }

  /** DELETE api/v2/artist/special-event-pricing/{slug}/ */
  static async deleteSpecialEventPricing(slug: string): Promise<void> {
    await apiClient.delete(
      API_ENDPOINTS.AUTH.ARTIST_SPECIAL_EVENT_PRICING(slug)
    );
  }

  /** GET api/v2/artist/tech-rider-tbl/ */
  static async getTechRiderTbl(): Promise<TechRiderTblData> {
    const response = await apiClient.get<{
      success: boolean;
      data: TechRiderTblData;
    }>(API_ENDPOINTS.AUTH.ARTIST_TECH_RIDER_TBL);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return {
      tbl: false,
      tbl_desc: "",
      tech_rider: false,
      tech_rider_desc: "",
    };
  }

  /** PUT api/v2/artist/tech-rider-tbl/ */
  static async putTechRiderTbl(
    payload: TechRiderTblPatchPayload
  ): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_TECH_RIDER_TBL, payload);
  }

  // --- Contact (primary verify OTP, modification request; secondary update) ---

  /** POST Send OTP to primary email for verification */
  static async sendContactEmailOtp(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_OTP_EMAIL_SEND, {});
  }

  /** POST Verify primary email with OTP */
  static async verifyContactEmailOtp(otp: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_OTP_EMAIL_VERIFY, {
      otp,
    });
  }

  /** POST Send OTP to primary phone for verification */
  static async sendContactPhoneOtp(): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_OTP_PHONE_SEND, {});
  }

  /** POST Verify primary phone with OTP */
  static async verifyContactPhoneOtp(otp: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_OTP_PHONE_VERIFY, {
      otp,
    });
  }

  /** POST api/v2/artist/contact-details/ – request primary email change (under review) */
  static async requestContactEmailModification(newEmail: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_DETAILS, {
      primary: [{ email: newEmail.trim().toLowerCase() }],
    });
  }

  /** POST api/v2/artist/contact-details/ – request primary mobile change (under review) */
  static async requestContactPhoneModification(newPhone: string): Promise<void> {
    await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_DETAILS, {
      primary: [{ mobile: newPhone.replace(/\s+/g, '').trim() }],
    });
  }

  /** PUT Update secondary contact (email, phone) */
  static async updateSecondaryContact(payload: {
    secondary_email?: string;
    secondary_phone?: string;
  }): Promise<void> {
    await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_CONTACT_SECONDARY, payload);
  }

  /** POST api/v2/artist/contact-us/ */
  static async postContactUs(payload: ContactUsPayload): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.ARTIST_CONTACT_US, payload);
    } catch (err: any) {
      const apiMessage: string | undefined =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message;

      if (apiMessage) {
        throw new Error(apiMessage);
      }

      throw err;
    }
  }

  /** POST api/v2/jobs/bids/{id}/ – submit a bid for a job */
  static async submitJobBid(
    jobId: number | string,
    payload: {
      bid: number;
      message: string;
      job_tbl?: string;
      job_tech_rider?: string;
    }
  ): Promise<void> {
    await apiClient.post(
      API_ENDPOINTS.AUTH.JOBS_BID_SUBMIT(jobId),
      payload
    );
  }

  // --- Billing ---

  /** GET api/v2/artist/billing/ */
  static async getBilling(): Promise<BillingData | null> {
    const response = await apiClient.get<{
      success: boolean;
      data: BillingData;
    }>(API_ENDPOINTS.AUTH.ARTIST_BILLING);
    if (response.data?.success && response.data?.data) {
      return response.data.data;
    }
    return null;
  }

  /** PUT api/v2/artist/billing/gst-details/ */
  static async putGstDetails(payload: GstDetailsPayload): Promise<void> {
    await apiClient.put(
      API_ENDPOINTS.AUTH.ARTIST_BILLING_GST_DETAILS,
      payload
    );
  }

  /** POST api/v2/artist/billing/bank-accounts/ */
  static async postBankAccount(
    payload: BankAccountCreatePayload
  ): Promise<void> {
    await apiClient.post(
      API_ENDPOINTS.AUTH.ARTIST_BILLING_BANK_ACCOUNTS,
      payload
    );
  }

  /** PATCH api/v2/artist/billing/bank-accounts/{id}/ */
  static async patchBankAccount(
    id: number,
    payload: BankAccountPatchPayload
  ): Promise<void> {
    await apiClient.patch(
      API_ENDPOINTS.AUTH.ARTIST_BILLING_BANK_ACCOUNT(id),
      payload
    );
  }

  /** DELETE api/v2/artist/billing/bank-accounts/{id}/ */
  static async deleteBankAccount(id: number): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.AUTH.ARTIST_BILLING_BANK_ACCOUNT(id));
  }
}

export interface EventPricingApiEvent {
  id: number;
  event_name: string;
  e_slug: string;
}

export interface EventPricingApiItem {
  event: EventPricingApiEvent;
  base_price: string;
  festival_pricing: Record<string, string>;
}

export interface EventPricingApiResponse {
  success: boolean;
  data: EventPricingApiItem[];
}

export interface EventPricingCreatePayload {
  event_slug: string;
  base_price: string;
  festival_pricing: {
    christmas?: string | null;
    holi?: string | null;
    new_year?: string | null;
  };
}

export interface EventPricingUpdatePayload {
  base_price: string;
  apply_to_all: boolean;
  festival_pricing: Record<string, string>;
}

export interface TechRiderTblData {
  tbl: boolean;
  tbl_desc: string;
  tech_rider: boolean;
  tech_rider_desc: string;
}

export type TechRiderTblPatchPayload =
  | { tbl: false }
  | { tech_rider: false }
  | { tbl: true; tbl_desc: string }
  | { tech_rider: true; tech_rider_desc: string };

export interface StoryData {
  story: string;
  pending?: boolean;
}

export interface StoryPayload {
  story: string;
}

export interface UspData {
  usp: string;
  pending?: boolean;
}

export interface UspPayload {
  usp: string;
}

// GET returns location as array; we normalize to first element
export type PersonalDetailsLocationItem = {
  city_slug?: string | null;
  city: string;
  pin: string;
  locality: string;
  state: string;
};

export interface PersonalDetailsData {
  gender?: { gender: string; g_slug: string };
  pincode?: string;
  /** GET returns location as array [{ city, pin, locality, state }]; we use first element */
  location?: PersonalDetailsLocationItem | PersonalDetailsLocationItem[];
  genre?: { name: string; slug: string }[];
  category?: { name: string; slug: string };
}

// POST payload
export interface PersonalDetailsPayload {
  gender: string;
  pincode: string;
  genre: string[];
  location: Array<{ city: string; pin: string; locality: string; state: string }>;
}

// --- Billing ---

export interface BankingDetailApi {
  id: number;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch: string;
  bank_address: string;
  default: boolean;
  account_type: string | null;
}

export interface GstDetailsApi {
  id: number;
  gstin: string;
  pan: string;
  registered_address: string;
}

export interface BillingData {
  banking_details: BankingDetailApi[];
  gst_details: GstDetailsApi | null;
}

export interface GstDetailsPayload {
  gstin: string;
  pan: string;
  registered_address: string;
}

export interface BankAccountCreatePayload {
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  account_type: string;
  bank_name: string;
  branch: string;
  bank_address: string;
}

export interface BankAccountPatchPayload {
  account_holder_name?: string;
  account_number?: string;
  ifsc_code?: string;
  account_type?: string;
  bank_name?: string;
  branch?: string;
  bank_address?: string;
  default?: boolean;
}